import React, { useState } from "react";
import {
  StyledTable,
  TableContainer,
} from "../../../components/tables/styledComponents";
import { CustomTableCell } from "../../../components/tables/CustomTableCell";
import {
  CellType,
  FlaggableVote,
  VotingDataSourceType,
} from "../../../models/tableModels";
import { VOTING_COLUMN_TYPES } from "../constants";

import {
  getColumnTypeFromKeyOrValue,
  getUniqueFilters,
  applyFilter,
} from "../tableUtils";

import { useHandleFlagChange } from "../../../hooks/useHandleFlagChange";
import { MESSAGES } from "../../../messages";
import { VotingColumnType } from "../../../models/stewardshipModels";
import { TABLE_BASE_HEIGHT } from "../../../styles";
import { FlagKey, SubjectType } from "../../../models/workflowModels";
import { Filter } from "../../../types/PageFilterTypes";
import { FlagColor, FlagStatus } from "../../../enums/Status";
import {
  buildFlagOptions,
  FLAG_COLOR_TO_STATUS,
  FLAG_STATUS_TO_FLAG_KEY,
} from "../../../components/visuals/StatusFlag";
import { retrieveVotingRecordId } from "../../../components/common/Voting/utils";
import { VOTER_SUFFIX } from "../../../constants";

export const VotingTable: React.FC<{
  data: VotingDataSourceType[];
  handleShowVotingRecordModal: (recordId: string) => void;
  columns: VotingColumnType[];
}> = ({ data, handleShowVotingRecordModal, columns }) => {
  const [flagOptions, setFlagOptions] = useState<{ [key: string]: Filter[] }>(
    {},
  );
  const { handleFlagChange } = useHandleFlagChange(SubjectType.VOTING_RECORDS);

  const injectVoting = (
    columnKey: string,
    value: CellType,
    record: VotingDataSourceType,
  ): CellType => {
    const votingRecordId = retrieveVotingRecordId(record, columnKey);

    const flaggableVoteBase = value as FlaggableVote;

    /**
     * Updated options with selector names.
     * If the flag for the record has been updated, override the options.
     */
    const updatedOptions =
      flagOptions[votingRecordId] ||
      flaggableVoteBase.options.map((option) => ({
        ...option,
        selectorName: votingRecordId,
      }));

    /**
     * Display modal parameters for voting record.
     * This ensures the modal opens with the correct record ID.
     */
    const displayVotingRecordModalParams = {
      displayVotingRecordModal: handleShowVotingRecordModal,
      recordId: votingRecordId,
    };

    const flaggableVote = {
      ...flaggableVoteBase,
      onSelectOption: updateFlag,
      options: updatedOptions,
      displayVotingRecordModalParams,
    };

    return flaggableVote;
  };

  const injectValues = (
    columnKey: string,
    value: CellType,
    record: VotingDataSourceType,
  ): CellType => {
    let injectedValue = value;

    // Inject voting record dependencies to make it a flaggable vote
    if (columnKey.includes(VOTER_SUFFIX) && value !== MESSAGES.NOT_AVAILABLE) {
      injectedValue = injectVoting(columnKey, injectedValue, record);
    }

    return injectedValue;
  };

  const updateFlag = (flagOption: Filter): void => {
    handleFlagChange(flagOption);
    const flagStatus: FlagStatus =
      FLAG_COLOR_TO_STATUS[flagOption.key as FlagColor];
    const flagKey: FlagKey = FLAG_STATUS_TO_FLAG_KEY[flagStatus];
    setFlagOptions((prev) => ({
      ...prev,
      [flagOption.selectorName as string]: buildFlagOptions(flagKey),
    }));
  };

  return (
    <TableContainer>
      <StyledTable
        dataSource={data}
        pagination={{ pageSize: 30 }}
        hasEveryCellBorderBottom={true}
        scroll={{ y: TABLE_BASE_HEIGHT, x: "max-content" }}
      >
        {columns.map((column) => (
          <StyledTable.Column
            key={column.key}
            dataIndex={column.dataIndex}
            title={column.title}
            fixed={column.fixed}
            filters={
              column.hasFilters
                ? getUniqueFilters(data, column.key as string)
                : undefined
            }
            // @ts-expect-error onFilter is not expecting a custom function
            onFilter={
              column.hasFilters
                ? (value: CellType, record: VotingDataSourceType): boolean =>
                    applyFilter(record, value as string, column.key as string)
                : null
            }
            render={(
              value: CellType,
              record: VotingDataSourceType,
            ): JSX.Element => (
              <CustomTableCell
                value={injectValues(
                  column.key?.toString() || "",
                  value || MESSAGES.NOT_AVAILABLE,
                  record,
                )}
                type={
                  getColumnTypeFromKeyOrValue(
                    column.key as string,
                    value || MESSAGES.NOT_AVAILABLE,
                    VOTING_COLUMN_TYPES,
                  ).type
                }
              />
            )}
          />
        ))}
      </StyledTable>
    </TableContainer>
  );
};
